import { AfibCountryCodes } from 'content/products/ring';

export const POWER_PLUG_IDS = {
  AFIB_DETECTION: 'afibDetection',
  CARDIO_ADAPTABILITY: 'cardioAdaptability',
  CAFFEINE_WINDOW: 'caffeineWindow',
  PHASE_ALIGNMENT: 'phaseAlignment',
  TESLA_SYNC: 'teslaSync',
  VITAMIN_D: 'vitaminD',
  CYCLE_OVULATION: 'cycleOvulation',
  CYCLE_OVULATION_PREMIUM: 'cycleOvulationPremium',
  PREGNANCY_INSIGHTS: 'pregnancyInsights',
  SMART_ALARM: 'smartAlarm',
  SOCIAL_JETLAG: 'socialJetlag',
  NEW_PARENT: 'newParent',
  SCREENTIME: 'screentime',
  SHIFT_WORK: 'shiftWork',
} as const;

export type PowerPlugId = (typeof POWER_PLUG_IDS)[keyof typeof POWER_PLUG_IDS];

export type PowerPlugAvailability = {
  isAvailable: boolean;
};

export type PowerPlugAvailabilityMap = Record<PowerPlugId, PowerPlugAvailability>;

export const ALL_POWER_PLUG_IDS = Object.values(POWER_PLUG_IDS) as PowerPlugId[];

const AFIB_COUNTRY_CODES_SET = new Set(AfibCountryCodes);

const normalizeRegionCode = (region?: string | null) =>
  (region || '').toUpperCase().trim();

export const getPowerPlugAvailabilityByRegion = (
  region?: string | null,
): PowerPlugAvailabilityMap => {
  const regionCode = normalizeRegionCode(region);
  const isAfibAvailable =
    Boolean(regionCode) && AFIB_COUNTRY_CODES_SET.has(regionCode);
  const isCardioAdaptabilityAvailable = !isAfibAvailable;

  return ALL_POWER_PLUG_IDS.reduce((availabilityMap, plugId) => {
    availabilityMap[plugId] = {
      isAvailable:
        plugId === POWER_PLUG_IDS.AFIB_DETECTION
          ? isAfibAvailable
          : plugId === POWER_PLUG_IDS.CARDIO_ADAPTABILITY
            ? isCardioAdaptabilityAvailable
            : true,
    };
    return availabilityMap;
  }, {} as PowerPlugAvailabilityMap);
};

export const getAvailablePowerPlugIds = (
  availabilityMap: PowerPlugAvailabilityMap,
): PowerPlugId[] =>
  ALL_POWER_PLUG_IDS.filter((plugId) => availabilityMap[plugId].isAvailable);

export const getAvailablePowerPlugIdsByRegion = (
  region?: string | null,
): PowerPlugId[] => getAvailablePowerPlugIds(getPowerPlugAvailabilityByRegion(region));

export const isPowerPlugAvailableInRegion = (
  region: string | null | undefined,
  plugId: PowerPlugId,
): boolean => getPowerPlugAvailabilityByRegion(region)[plugId].isAvailable;
